import React, { useState } from 'react';
import { Toaster } from 'sonner';
import EnhancedAssignmentsTable from './EnhancedAssignmentsTable';
import EnhancedClassesTable from './EnhancedClassesTable';
import MessageCenter from 'components/communication/MessageCenter';
import UserCreationForm from 'components/modals/UserCreationForm';
import NotificationManager from 'components/notifications/NotificationManager';
import CommentSearch from 'components/admin/CommentSearch';
import DiscordConnectionPanel from 'components/discord/DiscordConnectionPanel';
import DiscordConnectionReminder from 'components/discord/DiscordConnectionReminder';
// import ProfileSettings from 'components/profile/ProfileSettings';
import ResponsiveSidebar from 'components/ui/ResponsiveSidebar';
import StickyNavbar from 'components/ui/StickyNavbar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useAutoTour } from 'hooks/useTours';

const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('assignments');
  const [showUserCreationModal, setShowUserCreationModal] = useState(false);
  const [showCommentSearch, setShowCommentSearch] = useState(false);
  const [showDiscordPanel, setShowDiscordPanel] = useState(false);
  // const [showProfileSettings, setShowProfileSettings] = useState(false);
  const { profile } = useAuth();

  // Auto-trigger admin dashboard tour for new admin users
  useAutoTour('admin-dashboard', {
    delay: 1000,
    firstVisit: true,
    userRole: 'admin'
  });

  const handleUserCreationSuccess = () => {
    setShowUserCreationModal(false);
    // Users will automatically appear in admin interfaces due to real-time listeners
  };

  const handleTabChange = (tabId) => {
    // Handle special tabs that open modals
    // switch (tabId) {
    //   case 'profile':
    //   case 'settings':
    //     setShowProfileSettings(true);
    //     break;
    //   default:
    //     setActiveTab(tabId);
    //     break;
    // }
    setActiveTab(tabId);
  };

  const handleActionClick = (action) => {
    switch (action) {
      case 'createUser':
        setShowUserCreationModal(true);
        break;
      case 'searchComments':
        setShowCommentSearch(true);
        break;
      case 'discordPanel':
        setShowDiscordPanel(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <StickyNavbar />
      <ResponsiveSidebar 
      currentTab={activeTab} 
      onTabChange={handleTabChange} 
      userRole="admin"
      onActionClick={handleActionClick}
    >
      {/* Content Area */}
      <div className="space-y-6" style={{padding: 'var(--ios-space-4)'}}>
        {/* Landing Page Widget - Hidden on messages tab */}
        {activeTab !== 'messages' && (
          <div className="ios-card-primary" style={{padding: 'var(--ios-space-4) var(--ios-space-4) var(--ios-space-5)', background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.1) 0%, rgba(255, 159, 10, 0.1) 100%), var(--ios-glass-primary)'}}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500/20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{borderRadius: 'var(--ios-radius-2xl)', boxShadow: 'var(--ios-shadow-sm)'}}>
                  <Icon name="Shield" size={20} className="text-red-400 sm:w-[22px] sm:h-[22px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="ios-text-primary font-semibold text-base sm:text-lg leading-tight" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-semibold)'}}>Admin Dashboard</h3>
                  <p className="ios-text-primary font-medium text-sm leading-tight mt-0.5 truncate" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-medium)'}}>{profile?.full_name}</p>
                  <p className="ios-text-secondary text-xs sm:text-sm leading-tight mt-1" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>Manage system settings</p>
                </div>
              </div>
              <a
                href="/"
                className="ios-button-primary px-4 py-2.5 flex items-center space-x-2 w-full sm:w-auto justify-center flex-shrink-0 text-sm font-medium"
                style={{
                  background: 'var(--ios-red)',
                  borderRadius: 'var(--ios-radius-xl)',
                  fontFamily: 'var(--ios-font-system)',
                  fontWeight: 'var(--ios-weight-semibold)',
                  transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
                }}
              >
                <Icon name="ExternalLink" size={15} />
                <span>Visit Landing Page</span>
              </a>
            </div>
          </div>
        )}

        {/* Content based on active tab */}
        {activeTab === 'assignments' && <EnhancedAssignmentsTable />}
        {activeTab === 'classes' && <EnhancedClassesTable />}
        {activeTab === 'messages' && (
          <div className="h-full">
            <MessageCenter />
          </div>
        )}
      </div>
      
      {/* Modals */}
      <UserCreationForm
        isOpen={showUserCreationModal}
        onClose={() => setShowUserCreationModal(false)}
        onSubmit={handleUserCreationSuccess}
      />
      
      <CommentSearch
        isOpen={showCommentSearch}
        onClose={() => setShowCommentSearch(false)}
      />
      
      {/* Discord Connection Panel */}
      <DiscordConnectionPanel
        isOpen={showDiscordPanel}
        onClose={() => setShowDiscordPanel(false)}
      />

      {/* Discord Connection Reminder */}
      <DiscordConnectionReminder
        onConnectClick={() => setShowDiscordPanel(true)}
      />
      
      {/* {showProfileSettings && (
        <ProfileSettings
          onClose={() => setShowProfileSettings(false)}
        />
      )} */}
      
      {/* Notifications */}
      <NotificationManager />
      
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 99999,
          },
        }}
      />
    </ResponsiveSidebar>
    </>
  );
};

export default AdminDashboardPage;